import React, { useEffect, useState, useCallback } from 'react';
import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';

/* Docs v1 — Table of contents.
   An ATOM node: it stores nothing but its own presence. The heading list is
   derived from the live document on every update, so it can never drift out of
   sync with the headings the way a stored copy would. Clicking an entry moves
   the selection to that heading and scrolls it into view. */

function TocView({ editor }) {
	const [headings, setHeadings] = useState([]);

	const collect = useCallback(() => {
		const found = [];
		editor.state.doc.descendants((node, pos) => {
			if (node.type.name === 'heading') {
				const text = node.textContent.trim();
				if (text) found.push({ level: node.attrs.level, text, pos });
			}
		});
		setHeadings(found);
	}, [editor]);

	useEffect(() => {
		collect();
		editor.on('update', collect);
		return () => {
			editor.off('update', collect);
		};
	}, [editor, collect]);

	const goTo = (pos) => {
		editor.chain().focus().setTextSelection(pos + 1).scrollIntoView().run();
	};

	return (
		<NodeViewWrapper className='cyv-toc' data-drag-handle>
			<div className='cyv-toc__label'>Table of contents</div>
			{headings.length === 0 ? (
				<div className='cyv-toc__empty'>
					Add headings to this doc and they’ll appear here.
				</div>
			) : (
				<ul className='cyv-toc__list'>
					{headings.map((h, i) => (
						<li key={`${h.pos}-${i}`} data-level={h.level}>
							<button type='button' onClick={() => goTo(h.pos)}>
								{h.text}
							</button>
						</li>
					))}
				</ul>
			)}
		</NodeViewWrapper>
	);
}

const TableOfContents = Node.create({
	name: 'tableOfContents',
	group: 'block',
	atom: true,
	selectable: true,
	draggable: true,

	parseHTML() {
		return [{ tag: 'div[data-toc]' }];
	},

	renderHTML() {
		return ['div', { 'data-toc': '', class: 'cyv-toc' }];
	},

	addNodeView() {
		return ReactNodeViewRenderer(TocView);
	},

	addCommands() {
		return {
			setTableOfContents:
				() =>
				({ commands }) =>
					commands.insertContent({ type: this.name }),
		};
	},
});

export default TableOfContents;
